import { formatScopePaths } from '../components/workflowShared';
import type {
  RunPageLoadState,
  RunStatusAgent,
  RunStatusDeduplication,
  RunStatusSnapshot
} from './RunPage';

interface RunStatusPageProps {
  status: RunStatusSnapshot | undefined;
  loadState: RunPageLoadState;
  error: string;
  isReady: boolean;
  issueCount: number;
  nowMs: number;
  onRetry: () => void;
  onViewResults: () => void;
}

interface RunSkillProgressItem {
  skill: string;
  label: string;
  status: string;
  progress: number;
  message: string;
  detail: string;
  error?: string;
}

interface RunTimeEstimate {
  estimateMinutes: number;
  elapsedText: string;
  remainingText: string;
}

const RUN_STATUS_TERMINAL_STATUSES = ['completed', 'partial', 'failed'];
const RUN_STATUS_SHORT_ESTIMATE_MINUTES = 10;
const RUN_STATUS_LONG_ESTIMATE_MINUTES = 30;
const RUN_STATUS_PROGRESS_CURVE_SEGMENTS = 8;
const RUN_STATUS_RUNNING_PROGRESS_CAP = 96;
const RUN_STATUS_SKILL_LABELS: Record<string, string> = {
  pashov: 'Pashov',
  'sc-auditor': 'SC Auditor',
  quillshield: 'QuillShield',
  cyfrin: 'Cyfrin',
  'trail-of-bits': 'Trail of Bits',
  'trail-of-bits-supply-chain': 'Trail of bits',
  'trail-of-bits-static-analysis': 'Trail of bits',
  'semgrep-code-security': 'Semgrep',
  'nemesis-auditor': 'Nemesis Auditor',
  forefy: 'Forefy'
};

const RUN_STATUS_PROGRESS_MESSAGES = {
  pending: [
    'Waiting for repository setup and an available scan worker.',
    'Queued for the selected AI models.',
    'Preparing the run context before this skill starts.'
  ],
  starting: [
    'Preparing skill instructions and scan context.',
    'Starting selected AI agents for this skill.',
    'Loading the scoped repository into this skill workflow.'
  ],
  mapping: [
    'Mapping contracts, tests, configuration, and dependency surfaces.',
    'Reading project structure and security-critical entry points.',
    'Building a quick view of the code paths this skill needs to inspect.'
  ],
  analyzing: [
    'Tracing privilege paths, value flows, and suspicious state changes.',
    'Checking known vulnerability patterns against the scoped code.',
    'Reviewing contract behavior and protocol assumptions for exploitable issues.'
  ],
  finalizing: [
    'Consolidating findings and formatting issue reports.',
    'Reviewing candidate findings before submission.',
    'Preparing structured output for deduplication and severity review.'
  ],
  completed: [
    'Finished. Findings are moving into report processing.',
    'Skill scan completed; results are being prepared for the report.',
    'Completed this skill pass and handed off candidate findings.'
  ],
  partial: [
    'Finished with partial results; completed model output will still be included.',
    'Some model output finished, and the report will use the available findings.',
    'Partial scan complete; usable findings are moving into report processing.'
  ],
  failed: [
    'This skill stopped before completing. Details are captured in the run logs.',
    'The scan worker hit an error for this skill.',
    'This skill could not finish, but the run will keep any available results.'
  ]
} as const;

const runStatusClamp = (value: number, min: number, max: number): number => {
  return Math.min(max, Math.max(min, value));
};

const runStatusHashString = (value: string): number => {
  return Array.from(value).reduce((hash, char) => {
    return ((hash << 5) - hash + char.charCodeAt(0)) >>> 0;
  }, 0);
};

const runStatusHashToUnit = (value: string): number => {
  let hash = runStatusHashString(value);

  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x7feb352d);
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x846ca68b);
  hash ^= hash >>> 16;

  return (hash >>> 0) / 0xffffffff;
};

const runStatusPickMessage = (messages: readonly string[], key: string): string => {
  return messages[runStatusHashString(key) % messages.length] ?? messages[0] ?? '';
};

const runStatusGetCurveWeight = (key: string, segmentIndex: number): number => {
  const unit = runStatusHashToUnit(`${key}:${segmentIndex}`);

  return 0.45 + unit * 1.35;
};

const runStatusApplyProgressCurve = (progressRatio: number, key: string): number => {
  const ratio = runStatusClamp(progressRatio, 0, 1);
  const segmentPosition = ratio * RUN_STATUS_PROGRESS_CURVE_SEGMENTS;
  const activeSegment = Math.min(
    RUN_STATUS_PROGRESS_CURVE_SEGMENTS - 1,
    Math.floor(segmentPosition)
  );
  const segmentRatio = segmentPosition - activeSegment;
  const easedSegmentRatio = segmentRatio * segmentRatio * (3 - (2 * segmentRatio));
  let totalWeight = 0;
  let progressedWeight = 0;

  for (let index = 0; index < RUN_STATUS_PROGRESS_CURVE_SEGMENTS; index += 1) {
    const weight = runStatusGetCurveWeight(key, index);

    totalWeight += weight;

    if (index < activeSegment) {
      progressedWeight += weight;
    } else if (index === activeSegment) {
      progressedWeight += weight * easedSegmentRatio;
    }
  }

  return totalWeight > 0 ? runStatusClamp(progressedWeight / totalWeight, 0, 1) : ratio;
};

const runStatusParseDateMs = (value: string | undefined): number | null => {
  if (!value) {
    return null;
  }

  const time = new Date(value).getTime();

  return Number.isNaN(time) ? null : time;
};

const runStatusFormatDuration = (durationMs: number): string => {
  if (durationMs < 60_000) {
    return '<1 min';
  }

  const totalMinutes = Math.max(1, Math.round(durationMs / 60_000));

  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};

const runStatusFormatLabel = (value: string | undefined): string => {
  if (!value) {
    return 'Unknown';
  }

  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const runStatusGetResultProcessingStatus = (
  status: RunStatusSnapshot | undefined
): RunStatusDeduplication | undefined => {
  return status?.deduplication ?? status?.report;
};

const runStatusGetEstimateMinutes = (skillCount: number): number => {
  return skillCount > 10 ? RUN_STATUS_LONG_ESTIMATE_MINUTES : RUN_STATUS_SHORT_ESTIMATE_MINUTES;
};

const runStatusGetTimeEstimate = (
  status: RunStatusSnapshot,
  nowMs: number,
  isReady: boolean
): RunTimeEstimate => {
  const estimateMinutes = runStatusGetEstimateMinutes(status.requestedSkills.length);
  const estimateMs = estimateMinutes * 60_000;
  const startedMs = runStatusParseDateMs(status.createdAt);
  const finishedMs = isReady ? runStatusParseDateMs(status.updatedAt) : null;
  const elapsedMs = startedMs === null ? 0 : Math.max(0, (finishedMs ?? nowMs) - startedMs);
  const remainingMs = Math.max(0, estimateMs - elapsedMs);

  return {
    estimateMinutes,
    elapsedText: runStatusFormatDuration(elapsedMs),
    remainingText: remainingMs > 0 ? `about ${runStatusFormatDuration(remainingMs)}` : 'finishing soon'
  };
};

const runStatusFormatSkillLabel = (skill: string): string => {
  return RUN_STATUS_SKILL_LABELS[skill] ?? runStatusFormatLabel(skill);
};

const runStatusGetFallbackModelCount = (status: RunStatusSnapshot): number => {
  return status.requestedAiAgentModels?.length ?? status.requestedAiAgents.length ?? 0;
};

const runStatusGetSkillProgressStatus = (
  status: RunStatusSnapshot,
  agent: RunStatusAgent | undefined
): string => {
  if (agent) {
    return agent.status;
  }

  if (status.setupError || status.status === 'failed') {
    return 'failed';
  }

  if (RUN_STATUS_TERMINAL_STATUSES.includes(status.status)) {
    return status.status;
  }

  return 'pending';
};

const runStatusGetSkillProgressPercent = (
  status: RunStatusSnapshot,
  skill: string,
  agent: RunStatusAgent | undefined,
  nowMs: number
): number => {
  const skillStatus = runStatusGetSkillProgressStatus(status, agent);

  if (RUN_STATUS_TERMINAL_STATUSES.includes(skillStatus)) {
    return 100;
  }

  const estimateMs = runStatusGetEstimateMinutes(status.requestedSkills.length) * 60_000;
  const runStartedMs = runStatusParseDateMs(status.createdAt) ?? nowMs;
  const runElapsedRatio = (nowMs - runStartedMs) / estimateMs;
  const curvedRunElapsedPercent = runStatusApplyProgressCurve(
    runElapsedRatio,
    `${status.runId}:${skill}:progress`
  ) * 100;
  const modelTotal = agent?.models.length ?? runStatusGetFallbackModelCount(status);
  const finishedModelCount = agent?.models.filter((model) => model.finished).length ?? 0;
  const modelPercent = modelTotal > 0 ? (finishedModelCount / modelTotal) * 100 : 0;

  if (!agent?.started) {
    return runStatusClamp(curvedRunElapsedPercent * 0.3, 0, 18);
  }

  return runStatusClamp(
    Math.max(4, curvedRunElapsedPercent, modelPercent),
    4,
    RUN_STATUS_RUNNING_PROGRESS_CAP
  );
};

const runStatusGetSkillMessageKey = (
  skillStatus: string,
  progress: number,
  isStarted: boolean
): keyof typeof RUN_STATUS_PROGRESS_MESSAGES => {
  if (skillStatus === 'completed') {
    return 'completed';
  }

  if (skillStatus === 'partial') {
    return 'partial';
  }

  if (skillStatus === 'failed') {
    return 'failed';
  }

  if (!isStarted) {
    return 'pending';
  }

  if (progress < 24) {
    return 'starting';
  }

  if (progress < 55) {
    return 'mapping';
  }

  if (progress < 84) {
    return 'analyzing';
  }

  return 'finalizing';
};

const runStatusGetSkillProgressItems = (status: RunStatusSnapshot, nowMs: number): RunSkillProgressItem[] => {
  const agentsBySkill = new Map(status.agents.map((agent) => [agent.agent, agent]));
  const requestedSkills = status.requestedSkills.length > 0
    ? status.requestedSkills
    : status.agents.map((agent) => agent.agent);

  return requestedSkills.map((skill) => {
    const agent = agentsBySkill.get(skill);
    const skillStatus = runStatusGetSkillProgressStatus(status, agent);
    const progress = runStatusGetSkillProgressPercent(status, skill, agent, nowMs);
    const messageKey = runStatusGetSkillMessageKey(skillStatus, progress, Boolean(agent?.started));
    const totalModels = agent?.models.length ?? runStatusGetFallbackModelCount(status);
    const finishedModels = agent?.models.filter((model) => model.finished).length ?? 0;
    const detail = totalModels > 0
      ? `${finishedModels}/${totalModels} model${totalModels === 1 ? '' : 's'} finished`
      : runStatusFormatLabel(skillStatus);

    return {
      skill,
      label: runStatusFormatSkillLabel(skill),
      status: skillStatus,
      progress,
      message: runStatusPickMessage(RUN_STATUS_PROGRESS_MESSAGES[messageKey], `${status.runId}:${skill}:${messageKey}`),
      detail,
      error: agent?.error
    };
  });
};

const runStatusGetSubtitle = (status: RunStatusSnapshot | undefined): string => {
  if (!status) {
    return 'Loading repository details...';
  }

  const scopePaths = status.scopePaths?.length
    ? status.scopePaths
    : status.scopePath ? [status.scopePath] : [];

  return `${status.target} - Directory: ${formatScopePaths(scopePaths)}`;
};

function RunStatusPage({
  status,
  loadState,
  error,
  isReady,
  issueCount,
  nowMs,
  onRetry,
  onViewResults
}: RunStatusPageProps) {
  const resultProcessing = runStatusGetResultProcessingStatus(status);
  const skillProgressItems = status ? runStatusGetSkillProgressItems(status, nowMs) : [];
  const timeEstimate = status ? runStatusGetTimeEstimate(status, nowMs, isReady) : null;

  return (
    <main className="run-workspace run-status-workspace">
      <section className="run-main">
        <div className="step-heading run-status-heading">
          <h1>Just A moment</h1>
          <p>{runStatusGetSubtitle(status)}</p>
        </div>

        {loadState === 'error' && (
          <div className="status-box error" role="alert">
            <strong>Could not load run data.</strong>
            <span>{error}</span>
            <button className="inline-action" type="button" onClick={onRetry}>
              Retry
            </button>
          </div>
        )}

        {!status && !isReady && loadState !== 'error' && (
          <p className="run-loading-note" role="status">
            <strong>{loadState === 'refreshing' ? 'Refreshing run data...' : 'Loading run data...'}</strong>
            <span>The page will keep polling until the scan and report generation steps are finished.</span>
          </p>
        )}

        {status?.setupError && (
          <div className="status-box error">
            <strong>Setup error</strong>
            <span>{status.setupError}</span>
          </div>
        )}

        {isReady && issueCount > 0 && (
          <div className="run-results-cta">
            <p>
              <strong>{issueCount} issue{issueCount !== 1 ? 's' : ''} found.</strong>
              {' '}Review the detailed findings in the results view.
            </p>
            <button
              type="button"
              className="primary-button"
              onClick={onViewResults}
            >
              View results →
            </button>
          </div>
        )}

        {status && (
          <section className="skill-progress-section" aria-labelledby="skill-progress-title">
            <div className="skill-progress-heading">
              <div>
                <h2 id="skill-progress-title">Skill progress</h2>
                {timeEstimate && (
                  <p>
                    Estimated total: about {timeEstimate.estimateMinutes} min - Elapsed {timeEstimate.elapsedText} - {isReady ? 'Complete' : `Remaining ${timeEstimate.remainingText}`}
                  </p>
                )}
              </div>
              <div className="run-progress-state" aria-label="Current run state">
                <span>{runStatusFormatLabel(status.status)}</span>
                <span>Report {runStatusFormatLabel(resultProcessing?.status ?? 'pending')}</span>
              </div>
            </div>

            <div className="skill-progress-list">
              {skillProgressItems.map((item) => (
                <div className={`skill-progress-row ${item.status}`} key={item.skill}>
                  <div className="skill-progress-row-header">
                    <div>
                      <h3>{item.label}</h3>
                      <span>{item.detail}</span>
                    </div>
                    <strong>{Math.round(item.progress)}%</strong>
                  </div>
                  <div
                    className="skill-progress-track"
                    role="progressbar"
                    aria-label={`${item.label} progress`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(item.progress)}
                  >
                    <div className="skill-progress-fill" style={{ width: `${item.progress}%` }} />
                  </div>
                  <p className="skill-progress-message">{item.message}</p>
                  {item.error && <p className="run-error-text">{item.error}</p>}
                </div>
              ))}
            </div>
          </section>
        )}
      </section>
    </main>
  );
}

export default RunStatusPage;
